import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {QlService} from '../../../core/shared/ql/service/ql.service';
import {QlQueryBuilder} from '../../../core/shared/ql/builder/ql-query-builder';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {XxPersonFlatRow} from './xx-person-external-flat-ql.service';
import {XxPersonDto} from '../model/dto/entity/xx-person.dto';
import {XxPersonDataDto} from '../model/dto/entity/xx-person-data.dto';
import {XxPersonTypeDto} from '../model/dto/entity/xx-person-type.dto';
import {XxPersonTypeFieldDto} from '../model/dto/entity/xx-person-type-field.dto';

/**
 * Everything an editing surface needs about ONE `xx.XxPersonType`, resolved in a single batched QL
 * request by {@link XxSidecarPlaygroundService#loadPersonTypeContextByName}.
 */
export interface XxPlaygroundPersonTypeContext {
    /** The resolved person type, or null when no type carries the requested name. */
    personType: XxPersonTypeDto | null;
    /** The persons typed with that person type. */
    persons: XxPersonDto[];
    /** The field definitions of that person type. */
    typeFields: XxPersonTypeFieldDto[];
}

/**
 * Result of a playground demo call: the exact QL query DTO that was sent to the
 * backend plus the rows it returned. The playground page renders BOTH so the
 * user can see the QL that produced the result.
 *
 * @ticket TM-1000001960
 */
export interface XxPlaygroundQueryRun {
    /** The QL query DTO that was executed (for display / copy). */
    query: any;
    /** The result rows for the query's pipe. */
    rows: any[];
    /** The raw, unwrapped backend response (for the "raw JSON" panel). */
    raw?: any;
}

/**
 * Data-layer service for the xx-module "Side-Car Playground" (TM-1000001960).
 *
 * Demonstrates QL calls against the full side-car pattern family on host
 * `xx.XxPerson`:
 * - #4 External Flat — bounded-external flatten with ONE join parameter
 *   (`external_party_version_id`) → `ext_<typeFieldId>` columns.
 * - #5 External Typed — bounded-external flatten with TWO join parameters
 *   (`external_party_version_id` + `type_id`) → `ext_<typeFieldId>` columns.
 * - #3 Internal Typed — read of `xx.XxPersonData`; the generic-attribute values
 *   surface as `gen_<typeFieldId>` fields.
 *
 * The {@link runQuery} helper executes an arbitrary query DTO and returns the
 * query together with its rows, so the page can display the QL that was sent.
 *
 * All HTTP goes through the QL framework — never a direct HttpClient call.
 *
 * NOTE: side-car FIELD VALUES (`cc.FieldValue`) are written through the backend
 * generic-attribute choke point (fed by the uf flow). The playground reads them;
 * populating them is the deferred uf step the user verifies once tenantA carries
 * the new DB version.
 *
 * @ticket TM-1000001960
 */
@Injectable({providedIn: 'root'})
export class XxSidecarPlaygroundService {

    constructor(private qlService: QlService) {}

    // ---------------------------------------------------------------------
    // Query builders (return the plain QL DTO so the page can display it)
    // ---------------------------------------------------------------------

    /**
     * Build the #4 External-Flat flatten query DTO (one join parameter).
     *
     * TM-1000001977 (version-axis split): `xx.XxPersonExternalFlat` is version-only
     * (`UNIQUE(xx_person_id, external_party_version_id)`, no assignment ref), so the
     * scope parameter is `external_party_version_id` via {@link QlQueryBuilder.boundedExternalVersionJoin}.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    buildExternalFlatQuery(versionId: number, typeFieldIds: number[]): any {
        return {
            name: 'externalFlat',
            type: 'view',
            start: {
                type: 'entity',
                name: 'xx.XxPerson',
                as: 'p',
                joins: [
                    QlQueryBuilder.boundedExternalVersionJoin('xx.XxPersonExternalFlat', 'ext', versionId, typeFieldIds)
                ]
            }
        };
    }

    /**
     * Build the #5 External-Typed flatten query DTO (TWO join parameters:
     * `external_party_version_id` + `type_id`).
     *
     * TM-1000001977 (version-axis split): `xx.XxPersonExternal` is scoped
     * `UNIQUE(xx_person_id, external_party_version_id, type_id)` with no assignment ref, so
     * the scope key migrated from `external_party_assignment_id` to `external_party_version_id`
     * (the additional `type_id` bound column is unchanged).
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    buildExternalTypedQuery(versionId: number, typeId: number, typeFieldIds: number[]): any {
        return {
            name: 'externalTyped',
            type: 'view',
            start: {
                type: 'entity',
                name: 'xx.XxPerson',
                as: 'p',
                joins: [
                    {
                        type: 'entity',
                        name: 'xx.XxPersonExternal',
                        as: 'ext',
                        joinType: 'left',
                        // bounded by (person, version, type) → BOTH params folded into the join ON
                        joinParameters: {external_party_version_id: versionId, type_id: typeId},
                        fields: typeFieldIds.map(id => ({name: String(id)}))
                    }
                ]
            }
        };
    }

    // ---------------------------------------------------------------------
    // Demo runner — runs an arbitrary query DTO + returns query AND rows
    // ---------------------------------------------------------------------

    /**
     * Execute an arbitrary QL query DTO and return the query DTO together with
     * its result rows and the raw response. Used by the playground to display
     * the exact QL that was sent next to the result.
     *
     * @param queryDto  the QL query DTO (e.g. from {@link buildExternalFlatQuery})
     * @param resultKey the query `name` to pull rows for (defaults to queryDto.name)
     * @param pipeType  'simple' for bounded-external flatten, 'object' for plain reads
     * @ticket TM-1000001960
     */
    runQuery(queryDto: any, resultKey?: string, pipeType: 'simple' | 'object' | 'view' = 'simple'): Observable<XxPlaygroundQueryRun> {
        const key = resultKey ?? queryDto?.name;
        return this.qlService.builder()
            .query(queryDto)
            .pipeType(pipeType)
            .execute()
            .pipe(
                map(result => ({
                    query: queryDto,
                    rows: result.get(key),
                    raw: result.getRawResponse()
                }))
            );
    }

    // ---------------------------------------------------------------------
    // Read paths per pattern
    // ---------------------------------------------------------------------

    /**
     * #5 External Typed flatten read — XxPerson + the typed side-car for one
     * (ExternalPartyVersion, XxPersonType) pair. Flattens the requested
     * type-field ids into `ext_<id>` columns via the two-parameter bounded join.
     *
     * TM-1000001977: scoped by the shared ExternalPartyVersion stream, not the assignment.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadPersonsWithExternalTyped(versionId: number, typeId: number, typeFieldIds: number[]): Observable<XxPersonFlatRow[]> {
        return this.runQuery(this.buildExternalTypedQuery(versionId, typeId, typeFieldIds), 'externalTyped', 'simple')
            .pipe(map(run => this.mapFlatRows(run.rows, typeFieldIds)));
    }

    /**
     * #3 Internal Typed read — read `xx.XxPersonData` rows. The generic-attribute
     * values surface as `gen_<typeFieldId>` fields on each row. Optionally filter
     * by the host business type.
     * @ticket TM-1000001960
     */
    loadPersonData(typeId?: number): Observable<any[]> {
        const query: any = {
            name: 'personData',
            type: 'view',
            start: {type: 'entity', name: 'xx.XxPersonData', as: 'e'}
        };
        if (typeId != null) {
            query.filters = [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, typeId)];
        }
        return this.qlService.builder()
            .query(query)
            .pipeType('object')
            .execute()
            .pipe(map(result => result.get('personData')));
    }

    // ---------------------------------------------------------------------
    // Selector loaders
    // ---------------------------------------------------------------------

    /**
     * Load all `cc.ExternalPartyAssignment` rows for the party selector.
     * @ticket TM-1000001960
     */
    loadExternalPartyAssignments(): Observable<any[]> {
        return this.qlService.builder()
            .query({name: 'assignments', type: 'view', start: {type: 'entity', name: 'cc.ExternalPartyAssignment', as: 'e'}})
            .pipeType('object')
            .execute()
            .pipe(map(result => result.get('assignments')));
    }

    /**
     * Load all `xx.XxPerson` rows for the person selector (uf tab). Returns the
     * raw person rows (id / firstName / lastName) so the caller can build a
     * "firstName lastName" label and default to the first person.
     * @ticket TM-1000001960
     */
    loadXxPersons(): Observable<any[]> {
        return this.qlService.builder()
            .query({name: 'persons', type: 'view', start: {type: 'entity', name: 'xx.XxPerson', as: 'e'}})
            .pipeType('object')
            .execute()
            .pipe(map(result => result.get('persons')));
    }

    /**
     * Load all `xx.XxPersonType` rows for the type selector (#3 / #5).
     * @ticket TM-1000001960
     */
    loadXxPersonTypes(): Observable<any[]> {
        return this.qlService.builder()
            .query({name: 'personTypes', type: 'view', start: {type: 'entity', name: 'xx.XxPersonType', as: 'e'}})
            .pipeType('object')
            .execute()
            .pipe(map(result => result.get('personTypes')));
    }

    /**
     * Resolve the complete context the "Berufe (TAA)" editing surface needs — the person type
     * itself, its persons and its field definitions — in ONE batched QL request.
     *
     * Batching, not `forkJoin`: the three reads are sequentially dependent (persons and field
     * definitions are both filtered by the id of the type resolved in the first query), and QL
     * expresses that natively through `dependsOn` plus the `${query:<name>:<alias>.<field>}`
     * variable. That keeps it a SINGLE HTTP round trip and removes the client-side orchestration
     * that combining separate QL calls would need.
     *
     * Name-based type resolution is deliberate: the playground must work on every environment, and
     * the database ids of the seeded demo rows differ per tenant, so a hardcoded id would break as
     * soon as the surface leaves the machine it was written on.
     *
     * When no type carries the given name the two dependent queries resolve their `IN` list to an
     * empty set and simply return no rows — that is not an error, it is the "type not seeded on this
     * environment" case the caller renders a hint for.
     *
     * Shape note: the `object` pipe returns ENTITY attribute names, so a foreign key arrives as
     * `typeId`, while the REST DTO of the same entity carries it as `typeDtoId`. The rows are typed
     * with the entity DTOs for their shared attributes (`id`, `name`, `firstName`, `lastName`,
     * `fieldOrder`) — which are the only ones this surface reads.
     *
     * @param typeName the type name to look for, e.g. `PLAYGROUND_TypeA`
     * @return the resolved type (or null), its persons and its field definitions
     */
    loadPersonTypeContextByName(typeName: string): Observable<XxPlaygroundPersonTypeContext> {
        return this.qlService.builder()
            .query({
                name: 'personTypeByName', type: 'view',
                start: {type: 'entity', name: 'xx.XxPersonType', as: 'e'},
                filters: [FilterCriteria.create('e.name', FilterCriteria.cOperatorEqual, typeName)]
            })
            .query({
                name: 'personsByType', type: 'view',
                dependsOn: ['personTypeByName'],
                start: {type: 'entity', name: 'xx.XxPerson', as: 'e'},
                filters: [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorIn, '${query:personTypeByName:e.id}')]
            })
            .query({
                name: 'personTypeFields', type: 'view',
                dependsOn: ['personTypeByName'],
                start: {type: 'entity', name: 'xx.XxPersonTypeField', as: 'e'},
                filters: [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorIn, '${query:personTypeByName:e.id}')]
            })
            .pipeType('object')
            .execute()
            .pipe(map(result => {

                const typeRows = (result.get('personTypeByName') ?? []) as XxPersonTypeDto[];

                return {
                    personType: typeRows.length > 0 ? typeRows[0] : null,
                    persons: (result.get('personsByType') ?? []) as XxPersonDto[],
                    typeFields: (result.get('personTypeFields') ?? []) as XxPersonTypeFieldDto[]
                };
            }));
    }

    /**
     * Resolve the internal typed side-car INSTANCE (`xx.XxPersonData`) of one person for one type.
     *
     * The instance is unique per `(person, type)` (unique index `xx_person_data_u1`), so at most one
     * row can come back. Returns null when the side-car has not been created yet — the caller then
     * renders the form in CREATE mode instead of EDIT mode.
     *
     * @param personId id of the host `xx.XxPerson`
     * @param typeId   id of the `xx.XxPersonType`
     */
    loadPersonDataForPerson(personId: number, typeId: number): Observable<XxPersonDataDto | null> {
        return this.qlService.builder()
            .query({
                name: 'personDataForPerson', type: 'view',
                start: {type: 'entity', name: 'xx.XxPersonData', as: 'e'},
                filters: [
                    FilterCriteria.create('e.xxPersonId', FilterCriteria.cOperatorEqual, personId),
                    FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, typeId)
                ]
            })
            .pipeType('object')
            .execute()
            .pipe(map(result => {
                const rows = (result.get('personDataForPerson') ?? []) as XxPersonDataDto[];
                return rows.length > 0 ? rows[0] : null;
            }));
    }

    /**
     * Load the `xx.XxPersonExternalFlatTypeField` ids for one version stream (#4).
     * Inter-query: resolve the flat type for the version, then its fields.
     *
     * TM-1000001977: `xx.XxPersonExternalFlatType` migrated its FK to the shared
     * `external_party_version_id`; the legacy `externalPartyAssignmentId` column was removed.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadFlatTypeFieldIds(versionId: number): Observable<number[]> {
        return this.qlService.builder()
            .query({
                name: 'flatTypes', type: 'view',
                start: {type: 'entity', name: 'xx.XxPersonExternalFlatType', as: 'e'},
                filters: [FilterCriteria.create('e.externalPartyVersionId', FilterCriteria.cOperatorEqual, versionId)]
            })
            .query({
                name: 'flatTypeFields', type: 'view',
                dependsOn: ['flatTypes'],
                start: {type: 'entity', name: 'xx.XxPersonExternalFlatTypeField', as: 'e'},
                filters: [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorIn, '${query:flatTypes:e.id}')]
            })
            .pipeType('object')
            .execute()
            .pipe(map(result => (result.get('flatTypeFields') as any[]).map(f => f.id as number)));
    }

    /**
     * Load the `xx.XxPersonExternalTypeField` ids for one (version, type)
     * pair (#5). Inter-query: resolve the typed external type for both keys,
     * then its fields.
     *
     * TM-1000001977: `xx.XxPersonExternalType` migrated its FK to the shared
     * `external_party_version_id`; the legacy `externalPartyAssignmentId` column was removed.
     * The `xxPersonTypeId` discriminator is unchanged.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadTypedTypeFieldIds(versionId: number, typeId: number): Observable<number[]> {
        return this.qlService.builder()
            .query({
                name: 'externalTypes', type: 'view',
                start: {type: 'entity', name: 'xx.XxPersonExternalType', as: 'e'},
                filters: [
                    FilterCriteria.create('e.externalPartyVersionId', FilterCriteria.cOperatorEqual, versionId),
                    FilterCriteria.create('e.xxPersonTypeId', FilterCriteria.cOperatorEqual, typeId)
                ]
            })
            .query({
                name: 'externalTypeFields', type: 'view',
                dependsOn: ['externalTypes'],
                start: {type: 'entity', name: 'xx.XxPersonExternalTypeField', as: 'e'},
                filters: [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorIn, '${query:externalTypes:e.id}')]
            })
            .pipeType('object')
            .execute()
            .pipe(map(result => (result.get('externalTypeFields') as any[]).map(f => f.id as number)));
    }

    // ---------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------

    /**
     * Normalize a bounded-external `simple`-pipe result into {@link XxPersonFlatRow}s:
     * `p.id`/`p.firstName`/`p.lastName` → top-level; `p.ext_<id>` → `ext_<id>`.
     * @ticket TM-1000001960
     */
    private mapFlatRows(rows: any[], typeFieldIds: number[]): XxPersonFlatRow[] {
        return (rows ?? []).map((row: any): XxPersonFlatRow => {
            const flat: XxPersonFlatRow = {
                id: row['p.id'] as number,
                firstName: row['p.firstName'] as string | undefined,
                lastName: row['p.lastName'] as string | undefined
            };
            for (const typeFieldId of typeFieldIds) {
                flat[`ext_${typeFieldId}`] = row[`p.ext_${typeFieldId}`];
            }
            return flat;
        });
    }
}
